import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError, tap, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from 'src/app/auth/auth.service';
import { AnalyticsEvent } from '../analytics/models/analytics-event';
import { AnalyticsService } from '../analytics/analytics.service';

export interface AddStockToStoreDto {
  orderId: number;
  productId: number;
  storeId: number;
  quantity: number;
  notes?: string;
  dot?: string;
  entryDate?: string;
  lotCode?: string;
  lotNotes?: string;
  location?: string;
  locations?: string[];
  cost: number;
}

export interface ReserveStockForAgendaDto {
  orderId: number;
  productId: number;
  storeId: number;
  quantity: number;
  agendaIds: number[];
  notes?: string;
  cost: number;
}

export interface StockAssignmentResponseDto {
  success: boolean;
  message: string;
  updatedStock?: any;
  reservations?: AgendaStockReservedDto[];
  warnings: string[];
}

export interface AvailableAgendaDto {
  id: number;
  clientFullName: string;
  serviceType: string;
  scheduledDate: string;
  status: string;
  notes?: string;
  isEligibleForReservation: boolean;
  existingReservations: AgendaStockReservedDto[];
}

export interface AgendaStockReservedDto {
  id: number;
  agendaId: number;
  productId: number;
  storeId?: number;
  quantity: number;
  isActive: boolean;
  createdAt: string;
}

export interface GetAvailableAgendasParams {
  centerId?: number;
  fromDate?: string;
  toDate?: string;
  status?: string;
  productId?: number;
  storeId?: number;
}

export interface UpdateOrderProductStatusDTO {
  productId: number;
  storeId: number;
  stockQuantity: number;
  dot: string;
  price: number;
  locations?: string[];
  appointmentId?: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class StockAssignmentService {
  private readonly baseUrl = `${environment.cafler_backoffice_back_url}/stock-assignment`;
  private readonly url = environment.cafler_backoffice_back_url;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly analytics: AnalyticsService
  ) { }

  updateOrderProductStatus(orderId: number, request: UpdateOrderProductStatusDTO): Observable<void> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap(token => {
        if (!token) {
          throw new Error('No token available');
        }
        return this.http.put<void>(`${this.url}/orders/${orderId}`, request, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }),
      tap((resp: void) => {

        if (request.appointmentId) {
          this.analytics.track(AnalyticsEvent.OrderReserveForAgendaSuccess, {
            ...this.analytics.globalProps(),
            order_id: orderId,
            appointment_id: request.appointmentId,
            items_count: request.stockQuantity
          });

        } else {
          this.analytics.track(AnalyticsEvent.OrderAddToWarehouseSuccess, {
            ...this.analytics.globalProps(),
            order_id: orderId,
            warehouse_id: request.storeId,
            items_count: request.stockQuantity
          });
        }
      }),
      catchError((err: HttpErrorResponse) => {

        this.handleError(err)

        const errorCode = err.error?.statusCode ?? 'UNKNOWN_ERROR';
        const errorMessage = err.error?.message ?? err.statusText ?? 'Unknown error';

        if (request.appointmentId) {
          this.analytics.track(AnalyticsEvent.OrderReserveForAgendaFail, {
            ...this.analytics.globalProps(),
            order_id: orderId,
            appointment_id: request.appointmentId,
            error_code: errorCode,
            error_message: errorMessage,
            http_status: err.status
          });

        } else {
          this.analytics.track(AnalyticsEvent.OrderAddToWarehouseFail, {
            ...this.analytics.globalProps(),
            order_id: orderId,
            warehouse_id: request.storeId,
            error_code: errorCode,
            error_message: errorMessage,
            http_status: err.status
          });

        }
        return throwError(() => err);

      })
    );
  }

  // addStockToStore(request: AddStockToStoreDto): Observable<StockAssignmentResponseDto> {
  //   return from(this.authService.getAccessToken()).pipe(
  //     switchMap(token => {
  //       if (!token) {
  //         throw new Error('No token available');
  //       }
  //       return this.http.post<StockAssignmentResponseDto>(`${this.baseUrl}/store`, request, {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           'Content-Type': 'application/json'
  //         }
  //       });
  //     }),
  //     catchError(this.handleError)
  //   );
  // }

  // reserveStockForAgenda(request: ReserveStockForAgendaDto): Observable<StockAssignmentResponseDto> {
  //   return from(this.authService.getAccessToken()).pipe(
  //     switchMap(token => {
  //       if (!token) {
  //         throw new Error('No token available');
  //       }
  //       return this.http.post<StockAssignmentResponseDto>(`${this.baseUrl}/agenda`, request, {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           'Content-Type': 'application/json'
  //         }
  //       });
  //     }),
  //     catchError(this.handleError)
  //   );
  // }

  getAvailableAgendas(params?: GetAvailableAgendasParams): Observable<AvailableAgendaDto[]> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap(token => {
        if (!token) {
          throw new Error('No token available');
        }

        let httpParams = new HttpParams();

        if (params?.centerId) {
          httpParams = httpParams.set('centerId', params.centerId.toString());
        }
        if (params?.fromDate) {
          httpParams = httpParams.set('fromDate', params.fromDate);
        }
        if (params?.toDate) {
          httpParams = httpParams.set('toDate', params.toDate);
        }
        if (params?.status) {
          httpParams = httpParams.set('status', params.status);
        }
        if (params?.productId) {
          httpParams = httpParams.set('productId', params.productId.toString());
        }
        if (params?.storeId) {
          httpParams = httpParams.set('storeId', params.storeId.toString());
        }

        return this.http.get<AvailableAgendaDto[]>(`${this.baseUrl}/available-agendas`, {
          params: httpParams,
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }),
      map((agendas) => agendas.map(agenda => ({
        ...agenda,
        clientFullName: agenda.clientFullName ?? (agenda as { clientName?: string }).clientName ?? ''
      }))),
      catchError(this.handleError)
    );
  }

  getStockReservations(productId: number, storeId: number): Observable<AgendaStockReservedDto[]> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap(token => {
        if (!token) {
          throw new Error('No token available');
        }

        const params = new HttpParams()
          .set('productId', productId.toString())
          .set('storeId', storeId.toString());

        return this.http.get<AgendaStockReservedDto[]>(`${this.baseUrl}/reservations`, {
          params,
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }),
      catchError(this.handleError)
    );
  }

  updateReservation(reservationId: number, request: any): Observable<AgendaStockReservedDto> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap(token => {
        if (!token) {
          throw new Error('No token available');
        }
        return this.http.put<AgendaStockReservedDto>(`${this.baseUrl}/reservations/${reservationId}`, request, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }),
      catchError(this.handleError)
    );
  }

  deleteReservation(reservationId: number): Observable<void> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap(token => {
        if (!token) {
          throw new Error('No token available');
        }
        return this.http.delete<void>(`${this.baseUrl}/reservations/${reservationId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }),
      catchError(this.handleError)
    );
  }

  private handleError(error: HttpErrorResponse) {
    console.error('Stock Assignment API Error:', error);
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = `Error Code: ${error.status}, Message: ${error.message}`;
    }

    return throwError(() => new Error(errorMessage));
  }
} 
